import React, { useState, useEffect } from 'react';
import { getItemAnalytics, getInventories, exportItemAnalyticsExcel } from '../lib/api';
import { downloadBlob } from '../lib/downloadHelper';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Package,
  Loader2,
  DollarSign,
  TrendingUp,
  BarChart3,
  ShoppingCart,
  Download,
  Printer
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CHART_MARGIN = { left: 10, right: 20 };
const CHART_TICK = { fill: '#374151' };
const TOOLTIP_STYLE = { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', boxShadow: '0 4px 12px -2px rgba(0,0,0,0.1)' };

const ItemAnalytics = () => {
  const [data, setData] = useState(null);
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInventory, setSelectedInventory] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportingIA, setExportingIA] = useState(false);

  useEffect(() => {
    getInventories().then(res => setInventories(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedInventory, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await getItemAnalytics(params);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load item analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
      </div>
    );
  }

  const usageChartData = data.top_used_items.map(item => ({
    name: item.item_name.length > 18 ? item.item_name.substring(0, 18) + '...' : item.item_name,
    fullName: item.item_name,
    quantity: item.total_quantity_used
  }));

  const costChartData = data.top_cost_items.map(item => ({
    name: item.item_name.length > 18 ? item.item_name.substring(0, 18) + '...' : item.item_name,
    fullName: item.item_name,
    cost: item.total_cost_used
  }));

  return (
    <div className="space-y-6 animate-slide-in" data-testid="item-analytics-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Item Analytics</h1>
          <p className="text-[#6B7280]">Analyze item usage patterns and cost trends</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button onClick={async () => {
            setExportingIA(true);
            try {
              const params = {};
              if (selectedInventory !== 'all') params.inventory_id = selectedInventory;
              if (startDate) params.start_date = startDate;
              if (endDate) params.end_date = endDate;
              const res = await exportItemAnalyticsExcel(params);
              downloadBlob(res.data, `item_analytics_${new Date().toISOString().split('T')[0]}.xlsx`);
            } catch (e) { void 0; alert('Export failed.'); } finally { setExportingIA(false); }
          }} disabled={exportingIA}
            className="gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg" data-testid="export-ia-btn">
            {exportingIA ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={18} />}
            Export Excel
          </Button>
          <Button onClick={() => window.print()} variant="outline"
            className="gap-2 rounded-lg border-[#0776BD] text-[#0776BD]" data-testid="print-ia-btn">
            <Printer size={18} /> Print
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-[#E6F2FA] rounded-xl">
                <Package size={22} className="text-[#0776BD]" />
              </div>
              <div>
                <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Items Used</p>
                <p className="text-2xl font-bold font-heading text-[#1F2937]" data-testid="unique-items-used">{data.unique_items_used}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-100 rounded-xl">
                <DollarSign size={22} className="text-red-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Total Usage Cost</p>
                <p className="text-2xl font-bold font-heading text-[#1F2937]" data-testid="total-usage-cost">${data.total_usage_cost.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-100 rounded-xl">
                <ShoppingCart size={22} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Items Purchased</p>
                <p className="text-2xl font-bold font-heading text-[#1F2937]" data-testid="unique-items-purchased">{data.unique_items_purchased}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-[#FFF8E1] rounded-xl">
                <TrendingUp size={22} className="text-[#F0C500]" />
              </div>
              <div>
                <p className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Total Purchase Cost</p>
                <p className="text-2xl font-bold font-heading text-[#1F2937]" data-testid="total-purchase-cost">${data.total_purchase_cost.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={selectedInventory} onValueChange={setSelectedInventory}>
              <SelectTrigger className="w-full md:w-[200px] h-12 rounded-lg border-gray-300" data-testid="ia-inventory-filter">
                <SelectValue placeholder="All Inventories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inventories</SelectItem>
                {inventories.map(inv => (
                  <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 flex-1">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-12 rounded-lg border-gray-300 text-sm"
                data-testid="ia-start-date"
              />
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-12 rounded-lg border-gray-300 text-sm"
                data-testid="ia-end-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Used Items Chart */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
              <BarChart3 size={20} className="text-[#0776BD]" />
              Top 10 Most Used Items (by Quantity)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usageChartData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={usageChartData} layout="vertical" margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" stroke="#6B7280" fontSize={12} />
                    <YAxis dataKey="name" type="category" width={120} stroke="#6B7280" fontSize={11} tick={CHART_TICK} />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value, name, props) => [`${value} units`, props.payload.fullName]}
                    />
                    <Bar dataKey="quantity" fill="#0776BD" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-[#6B7280]">No usage data</div>
            )}
          </CardContent>
        </Card>

        {/* Top Cost Items Chart */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
              <DollarSign size={20} className="text-[#F0C500]" />
              Top 10 Highest Cost Items (by Spend)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {costChartData.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={costChartData} layout="vertical" margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis type="number" stroke="#6B7280" fontSize={12} tickFormatter={(v) => `$${v}`} />
                    <YAxis dataKey="name" type="category" width={120} stroke="#6B7280" fontSize={11} tick={CHART_TICK} />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value, name, props) => [`$${value.toFixed(2)}`, props.payload.fullName]}
                    />
                    <Bar dataKey="cost" fill="#F0C500" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-[#6B7280]">No cost data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Used Items Table */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-base text-[#1F2937]">Most Used Items</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F6F7]">
                  <TableHead className="font-semibold text-[#1F2937]">#</TableHead>
                  <TableHead className="font-semibold text-[#1F2937]">Item</TableHead>
                  <TableHead className="font-semibold text-[#1F2937] text-center">Qty Used</TableHead>
                  <TableHead className="font-semibold text-[#1F2937] text-right">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.top_used_items.map((item, idx) => (
                  <TableRow key={item.item_id}>
                    <TableCell>
                      <Badge className={idx < 3 ? "bg-[#F0C500] text-[#1F2937] border-0" : "bg-gray-100 text-gray-600 border-0"}>
                        {idx + 1}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-[#1F2937]">{item.item_name}</div>
                      <div className="text-xs text-[#0776BD] font-mono">({item.internal_code})</div>
                    </TableCell>
                    <TableCell className="text-center font-medium text-[#1F2937]">{item.total_quantity_used}</TableCell>
                    <TableCell className="text-right text-[#6B7280]">${item.total_cost_used.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {data.top_used_items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-[#6B7280] py-8">No usage data</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Top Purchased Items Table */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-base text-[#1F2937]">Highest Cost Purchases</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F6F7]">
                  <TableHead className="font-semibold text-[#1F2937]">#</TableHead>
                  <TableHead className="font-semibold text-[#1F2937]">Item</TableHead>
                  <TableHead className="font-semibold text-[#1F2937] text-center">Qty Bought</TableHead>
                  <TableHead className="font-semibold text-[#1F2937] text-right">Spend</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.top_purchased_items.map((item, idx) => (
                  <TableRow key={item.item_id}>
                    <TableCell>
                      <Badge className={idx < 3 ? "bg-[#F0C500] text-[#1F2937] border-0" : "bg-gray-100 text-gray-600 border-0"}>
                        {idx + 1}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-[#1F2937]">{item.item_name}</div>
                      <div className="text-xs text-[#0776BD] font-mono">({item.internal_code})</div>
                    </TableCell>
                    <TableCell className="text-center font-medium text-[#1F2937]">{item.total_quantity_purchased}</TableCell>
                    <TableCell className="text-right text-[#6B7280]">${item.total_cost_purchased.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {data.top_purchased_items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-[#6B7280] py-8">No purchase data</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ItemAnalytics;
